import { useEffect, useState } from 'react';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { DataTable } from '@/components/common/DataTable';
import { TenantTopUpModal } from '@/components/features/wallet/TenantTopUpModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Wallet, Plus, Search, TrendingUp, ArrowDownCircle, ArrowUpCircle, RefreshCw } from 'lucide-react';
import { formatCurrency, formatDateTime } from '@/lib/formatters';

interface TenantWalletTransaction {
  id: string;
  tenant_id: string;
  tenant_name: string;
  type: 'deposit' | 'deduction' | 'transfer' | 'refund';
  amount: number;
  reference_type: 'shipment' | 'settlement' | 'manual' | 'topup';
  reference_id: string | null;
  description: string;
  created_at: string;
}

interface Meta {
  total: number;
  page: string;
  limit: string;
  totalPages: number;
}

interface Summary {
  total_deposited: number;
  total_deducted: number;
  total_transactions: number;
}

const TYPE_LABELS: Record<TenantWalletTransaction['type'], string> = {
  deposit:   'إيداع',
  deduction: 'خصم',
  transfer:  'تحويل',
  refund:    'استرداد',
};

const TYPE_COLORS: Record<TenantWalletTransaction['type'], string> = {
  deposit:   'bg-green-500/10 text-green-600 border-green-500/20',
  deduction: 'bg-red-500/10 text-red-600 border-red-500/20',
  transfer:  'bg-blue-500/10 text-blue-600 border-blue-500/20',
  refund:    'bg-orange-500/10 text-orange-600 border-orange-500/20',
};

export default function WalletTopUp() {
  const { get, loading } = useApi();
  const [transactions, setTransactions] = useState<TenantWalletTransaction[]>([]);
  const [meta, setMeta] = useState<Meta>({ total: 0, page: '1', limit: '10', totalPages: 0 });
  const [summary, setSummary] = useState<Summary>({ total_deposited: 0, total_deducted: 0, total_transactions: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, [page]);

  const fetchTransactions = async () => {
    try {
      const response: any = await get('/api/v1/dashboard/wallet/transactions', {
        page,
        limit: 10,
        ...(search.trim() && { search: search.trim() }),
      });
      const data = response?.data || response || {};
      setTransactions(Array.isArray(data.list) ? data.list : []);
      if (data.meta) setMeta(data.meta);
      if (data.summary) setSummary(data.summary);
    } catch {
      // error toast handled by useApi
    } finally {
      setHasFetched(true);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchTransactions();
  };


  const columns = [
    {
      header: 'التاجر',
      accessor: (row: TenantWalletTransaction) => (
        <span className="font-medium">{row.tenant_name || '—'}</span>
      ),
    },
    {
      header: 'رقم الشحنة',
      accessor: (row: TenantWalletTransaction) =>
        row.reference_type === 'shipment' && row.reference_id ? (
          <span className="font-mono text-sm">{row.reference_id}</span>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        ),
    },
    {
      header: 'النوع',
      accessor: (row: TenantWalletTransaction) => (
        <Badge variant="outline" className={TYPE_COLORS[row.type]}>
          {TYPE_LABELS[row.type]}
        </Badge>
      ),
    },
    {
      header: 'المبلغ',
      accessor: (row: TenantWalletTransaction) => (
        <span
          className={`font-semibold ${
            row.type === 'deposit' || row.type === 'refund'
              ? 'text-green-600'
              : 'text-red-600'
          }`}
          dir="ltr"
        >
          {row.type === 'deposit' || row.type === 'refund' ? '+' : '-'}
          {formatCurrency(row.amount)}
        </span>
      ),
    },
    {
      header: 'الوصف',
      accessor: (row: TenantWalletTransaction) => (
        <span className="text-sm text-muted-foreground">{row.description || '—'}</span>
      ),
    },
    {
      header: 'التاريخ',
      accessor: (row: TenantWalletTransaction) => (
        <span className="text-sm text-muted-foreground" dir="ltr">
          {formatDateTime(row.created_at)}
        </span>
      ),
    },
  ];

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Wallet className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">المحافظ — إيداع الرصيد</h1>
            <p className="text-sm text-muted-foreground">
              إدارة محافظ التجار وعمليات الإيداع
            </p>
          </div>
        </div>
        <Button onClick={() => setModalOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          إيداع رصيد
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border bg-card p-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">إجمالي العمليات</p>
            <p className="text-lg font-bold">{summary.total_transactions}</p>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-950">
            <ArrowUpCircle className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">إجمالي الإيداعات</p>
            <p className="text-lg font-bold text-green-600" dir="ltr">
              {formatCurrency(summary.total_deposited)}
            </p>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 dark:bg-red-950">
            <ArrowDownCircle className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">إجمالي الخصومات</p>
            <p className="text-lg font-bold text-red-600" dir="ltr">
              {formatCurrency(summary.total_deducted)}
            </p>
          </div>
        </div>
      </div>

      {/* Search + Refresh */}
      <div className="mb-4 flex items-center gap-3">
        <form onSubmit={handleSearch} className="flex flex-1 items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="بحث باسم التاجر أو رقم الشحنة..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pe-9"
            />
          </div>
          <Button type="submit" variant="secondary" size="sm">
            بحث
          </Button>
        </form>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => { setPage(1); fetchTransactions(); }}
          title="تحديث"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        {!hasFetched || loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <DataTable
            data={transactions}
            columns={columns}
            emptyMessage="لا توجد معاملات بعد"
            pagination={{
              currentPage: page,
              totalPages: meta.totalPages,
              total: meta.total,
              onPageChange: setPage,
            }}
          />
        )}
      </div>

      {/* Top-Up Modal */}
      <TenantTopUpModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSuccess={() => {
          setPage(1);
          fetchTransactions();
        }}
      />
    </PageContainer>
  );
}
